import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CloseButton,
  Form,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import {
  DocumentSubType,
  type MiscContent,
  MiscContentType,
  Role,
} from "@/briefpoint-client";
import CustomEditor from "@/components/Editor/CustomEditor";
import LeavingGuard from "@/components/LeavingGuard";
import { FormModal } from "@/components/Modals";
import PlaceholderTutorialLink from "@/components/PlaceholderTutorialLink";
import { useAuth } from "@/hooks/useAuth";
import styles from "./Intro.module.scss";

interface Props {
  intros: MiscContent[];
  onUpdate: (update: MiscContent) => Promise<MiscContent>;
}

const subTypes = {
  1: "Interrogatories",
  2: "Requests for Production",
  3: "Requests for Admission",
};

export default function Intro({ intros, onUpdate }: Props) {
  const [content, setContent] = useState("");
  const { user, firm } = useAuth();
  const [isSharedWithFirm, setIsSharedWithFirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);
  const [nextSubType, setNextSubType] = useState<DocumentSubType | undefined>();
  const [toastMessage, setToastMessage] = useState("");
  const [selectedIntro, setSelectedIntro] = useState<MiscContent | null>();
  const [selectedIntroSubType, setSelectedIntroSubType] = useState<
    DocumentSubType | undefined
  >();

  const canSetFirmStandard = user?.role?.includes(Role.FirmAdmin);

  useEffect(() => {
    // Initialize with default intro if no subtype is selected
    if (!selectedIntroSubType && !selectedIntro) {
      const defaultIntro = intros.find((x) => !x.documentSubType);
      if (defaultIntro) {
        setSelectedIntro(defaultIntro);
        setContent(defaultIntro.content || "");
        setIsSharedWithFirm(!!defaultIntro.sharedWithFirmId);
        setIsDirty(false);
      }
    }
  }, [intros, selectedIntroSubType, selectedIntro]);

  useEffect(() => {
    // Update content when selectedIntro changes (but not during initial load)
    if (selectedIntro?.content !== undefined) {
      setContent(selectedIntro.content || "");
      setIsSharedWithFirm(!!selectedIntro.sharedWithFirmId);
      setIsDirty(false);
    }
  }, [selectedIntro]);

  function handleChangeContent(val: string) {
    if (val !== content) {
      setContent(val);
      checkIsDirty();
    }
  }

  async function onSave() {
    if (selectedIntro) {
      const update = {
        ...selectedIntro,
        content: content,
        sharedWithFirmId: isSharedWithFirm ? firm?.id : undefined,
      };
      const result = await onUpdate(update);
      setToastMessage("");
      setToastMessage("Intro Saved!");
      setIsDirty(false);
      setSelectedIntro(result);
    }
  }

  function handleChangeSelectedSubType(subType: DocumentSubType | undefined) {
    if (!isDirty) {
      handleSetSelectedIntroSubType(subType);
    } else {
      setNextSubType(subType);
      setShowDirtyConfirm(true);
    }
  }

  function handleSetSelectedIntroSubType(subType: DocumentSubType | undefined) {
    const next = subType ?? nextSubType;
    const intro = intros.find((x) => x.documentSubType === next);
    setSelectedIntro(intro);
    setSelectedIntroSubType(next);
    setShowDirtyConfirm(false);
    setNextSubType(undefined);
    setIsDirty(false);
  }

  function handleCreateOverrideClick() {
    const defaultText = intros.find((x) => !x.documentSubType);

    setSelectedIntro({
      content: defaultText?.content,
      documentType: defaultText?.documentType,
      type: MiscContentType.PreliminaryStatement,
      documentSubType: selectedIntroSubType,
    });
  }

  function handleChangeCancel() {
    setContent(selectedIntro?.content || "");
    setIsSharedWithFirm(!!selectedIntro?.sharedWithFirmId);
    setIsDirty(false);
  }

  function handleFirmStandardClick(checked: boolean) {
    setIsSharedWithFirm(checked);
    checkIsDirty();
  }

  function checkIsDirty() {
    const dirty =
      content !== selectedIntro?.content ||
      isSharedWithFirm !== !!selectedIntro?.sharedWithFirmId;
    setIsDirty(dirty);
  }

  return (
    <>
      <LeavingGuard
        when={isDirty}
        message="You have unsaved changes, continue and lose them?"
        title="Unsaved changes!"
      />
      <div
        className={`${styles.stepContainer} ${styles.introContainer}`}
        style={{ gap: 0 }}
      >
        <ToastContainer className="p-3" position="top-end">
          <Toast
            onClose={() => setToastMessage("")}
            show={!!toastMessage}
            delay={2500}
            bg="success"
            autohide
          >
            <Toast.Body>
              <div className="d-flex text-white">
                <div>{toastMessage}</div>
                <CloseButton
                  className="btn-close btn-close-white m-auto me-2"
                  onClick={() => setToastMessage("")}
                />
              </div>
            </Toast.Body>
          </Toast>
        </ToastContainer>

        <div className={styles.introSidebar}>
          {
            // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button
            <div
              key={"default"}
              onClick={() => {
                handleChangeSelectedSubType(undefined);
              }}
              className={clsx(styles.libraryObjection, {
                [styles.libraryObjectionSelected]: !selectedIntroSubType,
              })}
            >
              Default
            </div>
          }
          {Object.keys(DocumentSubType).map((o) => {
            const i = Number(o);
            const t = subTypes[i as keyof typeof subTypes];

            return (
              t && (
                // biome-ignore lint/a11y/useKeyWithClickEvents: TODO: replace with button
                <div
                  key={o}
                  onClick={() => {
                    handleChangeSelectedSubType(i);
                  }}
                  className={clsx(styles.libraryObjection, {
                    [styles.libraryObjectionSelected]:
                      i === selectedIntroSubType,
                  })}
                >
                  {t}
                </div>
              )
            );
          })}
        </div>
        <div className={styles.introContent}>
          {selectedIntro ? (
            <>
              {!canSetFirmStandard && !!selectedIntro.sharedWithFirmId && (
                <Alert variant="info">
                  {
                    "This Intro is set as your Firm's Standard, contact your Firm Admin if you need to make changes."
                  }
                </Alert>
              )}

              <Form.Group className="mb-4">
                <Form.Label>Content</Form.Label>
                <CustomEditor
                  handleChange={handleChangeContent}
                  editorContent={content}
                  showAdvancedOptions={true}
                  showMenu={true}
                  isDisabled={
                    !canSetFirmStandard && !!selectedIntro.sharedWithFirmId
                  }
                  isFullWidth={true}
                  isFullHeight={true}
                />
                {canSetFirmStandard && (
                  <Form.Check
                    id="sharedwithfirm"
                    type="checkbox"
                    label="Use as Firm Standard"
                    checked={isSharedWithFirm}
                    onChange={(e) => handleFirmStandardClick(e.target.checked)}
                  />
                )}
                <FormModal
                  title={"Abandon changes?"}
                  cancelText="Cancel"
                  confirmText="Yes, abandon"
                  show={showDirtyConfirm}
                  onClose={() => setShowDirtyConfirm(false)}
                  onConfirm={() => handleSetSelectedIntroSubType(undefined)}
                >
                  <div>
                    Are you sure you want to abandon your saved changes?
                  </div>
                </FormModal>
              </Form.Group>
              <PlaceholderTutorialLink />
              <div className="d-flex">
                <Button
                  variant="primary"
                  onClick={onSave}
                  disabled={!isDirty && !!selectedIntro.id}
                >
                  Save
                </Button>
                <Button
                  variant="link"
                  onClick={handleChangeCancel}
                  disabled={!isDirty && !!selectedIntro.id}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div>
              <p>This Document Type will use the Default Intro.</p>
              <Button onClick={handleCreateOverrideClick}>Create New</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
